import { PageSettingsContext } from '@/contexts';
import { DEFAULT_PAGE_SIZE, PAGE_DEFAULT } from '@amili-v3/amili-v3-design';
import isEqual from 'lodash/isEqual';
import omitBy from 'lodash/omitBy';
import qs from 'qs';
import { useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryParams } from '../interfaces';
import { useParseQueryString } from './useParseQueryString';
import { DefaultParams } from '@/queries/queryKeys';

// export interface Params
//   extends OrderParams,
//     ProjectQueryParams,
//     KitParams,
//     SequencingParams,
//     ExtractionParams,
//     BatchesParams,
//     ReportParams,
//     QueryParamGetKitSerials,
//     ProgramQueryParams,
//     EmailParams,
//     LogQueryString,
//     ManageTagParams,
//     AnalysisParams,
//     ProductQueryParams,
//     TenantParams,
//     ApiKeyParams {}

export const useQueryNavigate = <Params extends QueryParams | DefaultParams>(
  initParams?: Params,
) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qsParams = useParseQueryString<Params>();
  const { pageSettings, setPageSettings = () => {} } =
    useContext(PageSettingsContext);
  const pageSize = pageSettings?.[pathname];

  const {
    q: initQ = qsParams?.q ?? '',
    page: initPage = Number(qsParams.page) || PAGE_DEFAULT,
    take: initTake = pageSize || Number(qsParams.take) || DEFAULT_PAGE_SIZE,
  } = initParams || {};

  const handleNavigate = useCallback(
    (props: Params, remove?: (keyof Params)[]) => {
      const dynamicParams: Params = {
        ...qsParams,
        ...props,
        q: props.q || qsParams.q || initQ,
        page: props.page || qsParams.page || initPage,
        take: props.take || qsParams.take || initTake,
      };

      // Clean up newParams by removing undefined and empty values
      const newParams: Partial<Params> = omitBy(dynamicParams, value => {
        if (typeof value === 'boolean') return false;
        return !!value === false; // Exclude falsy values except for boolean
      });

      // Remove specified parameters if provided
      remove?.forEach(param => {
        delete newParams[param];
      });

      // Update page settings if needed
      setPageSettings({ ...pageSettings, [pathname]: Number(newParams.take) });

      // Avoid navigation if there are no changes
      if (isEqual(newParams, qsParams)) return;

      // Generate query string and navigate
      navigate(
        `${pathname}?${qs.stringify(newParams, { arrayFormat: 'brackets' })}`,
        { replace: true },
      );
    },
    [
      initQ,
      navigate,
      pathname,
      initPage,
      qsParams,
      initTake,
      pageSettings,
      setPageSettings,
    ],
  );

  // const handleNavigate = useCallback(
  //   (props: Params, remove?: (keyof Params)[]) => {
  //     const {
  //       q = qsParams.q || initQ,
  //       page = qsParams.page || initPage,
  //       take = qsParams.take || initTake,
  //       deleted = qsParams.deleted,
  //       roles = qsParams.roles,
  //       status = qsParams.status,
  //       startDate = qsParams.startDate,
  //       endDate = qsParams.endDate,
  //       batchId = qsParams.batchId,
  //       batchIds = qsParams.batchIds,
  //       tab = qsParams.tab,
  //       order = qsParams.order,

  //       // Order
  //       channelId = qsParams.channelId,
  //       productType = qsParams.productType,

  //       // Kit
  //       restricted = qsParams.restricted,
  //       isDelayed = qsParams.isDelayed,
  //       projectIds = qsParams.projectIds,
  //       orderDateFrom = qsParams.orderDateFrom,
  //       orderDateTo = qsParams.orderDateTo,
  //       orderConfirmedDateFrom = qsParams.orderConfirmedDateFrom,
  //       orderConfirmedDateTo = qsParams.orderConfirmedDateTo,
  //       kitReceivedDateFrom = qsParams.kitReceivedDateFrom,
  //       kitReceivedDateTo = qsParams.kitReceivedDateTo,

  //       // Sequencing
  //       sequencingPartner = qsParams.sequencingPartner,
  //       type = qsParams.type,

  //       //Report
  //       projectId = qsParams.projectId,
  //       qcStatus = qsParams.qcStatus,
  //       orderId = qsParams.orderId,

  //       // Email
  //       templateName = qsParams.templateName,
  //       //program
  //       program = qsParams.program,

  //       //logs
  //       location = qsParams.location,
  //       offset = qsParams.offset,
  //       nextToken = qsParams.nextToken,
  //       object = qsParams.object,
  //       action = qsParams.action,

  //       // Profile
  //       dataType = qsParams.dataType,
  //       resource = qsParams.resource,
  //       numberOfValueFrom = qsParams.numberOfValueFrom,
  //       numberOfValueTo = qsParams.numberOfValueTo,

  //       //extraction
  //       dateStartOfExtracted = qsParams.dateStartOfExtracted,
  //       dateEndOfExtracted = qsParams.dateEndOfExtracted,
  //       extractorTechnicianName = qsParams.extractorTechnicianName,

  //       // Product
  //       active = qsParams.active,
  //       //analysis
  //       dateOfCreationFrom = qsParams.dateOfCreationFrom,
  //       dateOfCreationTo = qsParams.dateOfCreationTo,

  //       dateOfSendAnalysisFrom = qsParams.dateOfSendAnalysisFrom,
  //       dateOfSendAnalysisTo = qsParams.dateOfSendAnalysisTo,

  //       //api-key
  //       orderField = qsParams.orderField,
  //     } = props;
  //     const newPage = (Number(page) !== 0 && page) || 1;
  //     const newParams: Params = { q, page: newPage, take };
  //     if (roles) newParams.roles = roles;
  //     if (deleted) newParams.deleted = deleted;
  //     if (status) newParams.status = status;
  //     if (tab) newParams.tab = tab;
  //     if (order) newParams.order = order;

  //     if (productType) newParams.productType = productType;
  //     if (channelId) newParams.channelId = channelId;
  //     if (startDate) newParams.startDate = startDate;
  //     if (endDate) newParams.endDate = endDate;

  //     if (restricted) newParams.restricted = restricted;

  //     if (batchId) newParams.batchId = batchId;
  //     if (batchIds) newParams.batchIds = batchIds;

  //     if (sequencingPartner) newParams.sequencingPartner = sequencingPartner;
  //     if (props.channelIds) newParams.channelIds = props.channelIds;
  //     if (type) newParams.type = type;

  //     if (projectId) newParams.projectId = projectId;
  //     if (qcStatus) newParams.qcStatus = qcStatus;
  //     if (props.amiliPIC) newParams.amiliPIC = props.amiliPIC;

  //     if (templateName) newParams.templateName = templateName;

  //     if (dataType) newParams.dataType = dataType;
  //     if (resource) newParams.resource = resource;
  //     if (numberOfValueFrom) newParams.numberOfValueFrom = numberOfValueFrom;
  //     if (numberOfValueTo) newParams.numberOfValueTo = numberOfValueTo;

  //     //programs
  //     if (program) newParams.program = props.program;

  //     if (active !== undefined) newParams.active = active;

  //     //logs
  //     if (location) newParams.location = location;
  //     if (offset) newParams.offset = offset;
  //     if (nextToken) newParams.nextToken = nextToken;
  //     if (object) newParams.object = object;
  //     if (action) newParams.action = action;

  //     //extraction
  //     if (dateStartOfExtracted)
  //       newParams.dateStartOfExtracted = dateStartOfExtracted;
  //     if (dateEndOfExtracted) newParams.dateEndOfExtracted = dateEndOfExtracted;
  //     if (extractorTechnicianName)
  //       newParams.extractorTechnicianName = extractorTechnicianName;
  //     //analysis
  //     if (dateOfCreationFrom) newParams.dateOfCreationFrom = dateOfCreationFrom;
  //     if (dateOfCreationTo) newParams.dateOfCreationTo = dateOfCreationTo;

  //     if (dateOfSendAnalysisFrom)
  //       newParams.dateOfSendAnalysisFrom = dateOfSendAnalysisFrom;
  //     if (dateOfSendAnalysisTo)
  //       newParams.dateOfSendAnalysisTo = dateOfSendAnalysisTo;
  //     if (orderId) newParams.orderId = orderId;

  //     //api-key
  //     if (orderField) {
  //       newParams.orderField = orderField;
  //     }

  //     //kit
  //     if (projectIds) {
  //       newParams.projectIds = projectIds;
  //     }
  //     if (orderDateFrom) newParams.orderDateFrom = orderDateFrom;
  //     if (orderDateTo) newParams.orderDateTo = orderDateTo;
  //     if (orderConfirmedDateFrom)
  //       newParams.orderConfirmedDateFrom = orderConfirmedDateFrom;
  //     if (orderConfirmedDateTo)
  //       newParams.orderConfirmedDateTo = orderConfirmedDateTo;
  //     if (kitReceivedDateFrom)
  //       newParams.kitReceivedDateFrom = kitReceivedDateFrom;
  //     if (kitReceivedDateTo) newParams.kitReceivedDateTo = kitReceivedDateTo;

  //     if (isDelayed) newParams.isDelayed = isDelayed;

  //     remove?.forEach(param => {
  //       delete newParams[param];
  //     });

  //     setPageSettings({ ...pageSettings, [pathname]: Number(take) });

  //     const result = omitBy(newParams, value => {
  //       if (typeof value === 'boolean') return false;
  //       return !!value === false;
  //     });
  //     if (isEqual(result, qsParams)) return;
  //     navigate(pathname + '?' + qs.stringify(result), { replace: true });
  //   },
  //   [
  //     initQ,
  //     navigate,
  //     pathname,
  //     initPage,
  //     qsParams,
  //     initTake,
  //     pageSettings,
  //     setPageSettings,
  //   ],
  // );

  return handleNavigate;
};
